import { useEffect, useMemo, useState } from 'react';
import { apiBinaryRequest, apiRequest } from '../../Utils/apiClient';

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const buildViewerQuery = ({ eventId, filePath, download = false } = {}) => {
  const params = new URLSearchParams();
  if (hasValue(eventId)) {
    params.set('id', String(eventId));
  } else if (hasValue(filePath)) {
    params.set('path', String(filePath));
  }
  if (download) {
    params.set('download', 'true');
  }
  return params.toString();
};

const parseHeaderFilename = (contentDisposition = '') => {
  const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      return encodedMatch[1];
    }
  }

  const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return filenameMatch?.[1] || '';
};

const decodeHeaderValue = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const readBlobWithProgress = async (response, onProgress) => {
  const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
  const contentLength = Number.parseInt(response.headers.get('Content-Length') || '0', 10);

  if (!response.body || typeof response.body.getReader !== 'function') {
    const blob = await response.blob();
    if (typeof onProgress === 'function') {
      onProgress(100);
    }
    return blob;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let receivedBytes = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    receivedBytes += value.length;

    if (contentLength > 0 && typeof onProgress === 'function') {
      onProgress(Math.min(100, (receivedBytes / contentLength) * 100));
    }
  }

  if (typeof onProgress === 'function') {
    onProgress(100);
  }
  return new Blob(chunks, { type: contentType });
};

export const fetchViewerData = async ({ eventId, filePath, signal } = {}) => {
  const query = buildViewerQuery({ eventId, filePath });
  if (!query) {
    throw new Error('File id or path is required');
  }
  return apiRequest(`/api/v1/viewer/data?${query}`, { signal });
};

export const fetchViewerFile = async ({ eventId, filePath, download = false, signal, onProgress } = {}) => {
  const query = buildViewerQuery({ eventId, filePath, download });
  if (!query) {
    throw new Error('File id or path is required');
  }

  const response = await apiBinaryRequest(`/api/v1/viewer/file?${query}`, { signal });
  const blob = await readBlobWithProgress(response, onProgress);
  const filename = parseHeaderFilename(response.headers.get('Content-Disposition') || '')
    || decodeHeaderValue(response.headers.get('X-Dataunfold-Filename') || '');

  return {
    blob,
    filename,
    contentType: response.headers.get('Content-Type') || blob.type || 'application/octet-stream',
    fileType: response.headers.get('X-Dataunfold-Viewer-Type') || '',
    eventId: response.headers.get('X-Dataunfold-Event-Id') || eventId,
    path: decodeHeaderValue(response.headers.get('X-Dataunfold-Path') || ''),
  };
};

export const useViewerFile = ({
  open,
  eventId,
  filePath,
  enabled = true,
  download = false,
  missingMessage = 'File id or path is required',
} = {}) => {
  const [fileBlob, setFileBlob] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const requestKey = useMemo(
    () => JSON.stringify({ eventId: eventId ?? null, filePath: filePath || '', download: Boolean(download) }),
    [eventId, filePath, download]
  );

  useEffect(() => {
    if (!open) {
      setFileBlob(null);
      setLoading(false);
      setProgress(0);
      setError(null);
      setMetadata(null);
      setFileUrl((previousUrl) => {
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        return null;
      });
      return undefined;
    }

    if (!enabled) {
      setFileBlob(null);
      setLoading(false);
      setProgress(0);
      setError(null);
      setMetadata(null);
      setFileUrl((previousUrl) => {
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        return null;
      });
      return undefined;
    }

    if (!hasValue(eventId) && !hasValue(filePath)) {
      setLoading(false);
      setError(missingMessage);
      return undefined;
    }

    const controller = new AbortController();
    setFileBlob(null);
    setLoading(true);
    setProgress(0);
    setError(null);
    setMetadata(null);
    setFileUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      return null;
    });

    fetchViewerFile({
      eventId,
      filePath,
      download,
      signal: controller.signal,
      onProgress: setProgress,
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        const url = URL.createObjectURL(result.blob);
        setFileBlob(result.blob);
        setFileUrl(url);
        setMetadata(result);
        setProgress(100);
        setLoading(false);
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return;
        setError(requestError?.message || 'Failed to load file');
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [open, enabled, requestKey, eventId, filePath, download, missingMessage]);

  useEffect(() => () => {
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }
  }, [fileUrl]);

  return {
    fileBlob,
    fileUrl,
    loading,
    progress,
    error,
    metadata,
    setError,
    setLoading,
  };
};

export const useViewerData = ({
  open,
  eventId,
  filePath,
  enabled = true,
  missingMessage = 'File id or path is required',
} = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const requestKey = useMemo(
    () => JSON.stringify({ eventId: eventId ?? null, filePath: filePath || '' }),
    [eventId, filePath]
  );

  useEffect(() => {
    if (!open) {
      setData(null);
      setLoading(false);
      setError(null);
      setMetadata(null);
      return undefined;
    }

    if (!enabled) {
      return undefined;
    }

    if (!hasValue(eventId) && !hasValue(filePath)) {
      setLoading(false);
      setError(missingMessage);
      return undefined;
    }

    const controller = new AbortController();
    setData(null);
    setLoading(true);
    setError(null);
    setMetadata(null);

    fetchViewerData({ eventId, filePath, signal: controller.signal })
      .then((payload) => {
        if (controller.signal.aborted) return;
        setData(payload?.data ?? null);
        setMetadata(payload || null);
        setLoading(false);
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return;
        setError(requestError?.message || 'Failed to load file');
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [open, enabled, requestKey, eventId, filePath, missingMessage]);

  return {
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    metadata,
  };
};
